import type { ReactNode } from "react";
import type { IsValidConnection } from "@xyflow/react";
import {
  FLOW_TYPES,
  resolveFlowType,
  type FlowShapeKind,
  type FlowType,
  type MindNode,
} from "@etus-mindmap/shared/api";

// Renderização das formas do fluxograma. Toda forma é desenhada como SVG de
// fundo com fill/stroke INLINE (o html-to-image não inlina apresentação SVG
// vinda de CSS → export sairia em branco). O rótulo fica num box acolchoado por
// cima. Só nós de flow usam isto; árvore/concept seguem no box clássico.

// Cor por token (tailwind-500). Usado no stroke/fill inline do SVG.
const HEX: Record<string, string> = {
  emerald: "#10b981",
  rose: "#f43f5e",
  slate: "#64748b",
  amber: "#f59e0b",
  sky: "#0ea5e9",
  violet: "#8b5cf6",
  cyan: "#06b6d4",
};

function colorHex(token: string): string {
  return HEX[token] ?? HEX.slate;
}

// Cor primária (respeita a personalização da org via CSS var). Usada em
// seleção/drop-target.
const PRIMARY = "rgb(var(--color-primary) / 1)";

// Padding do conteúdo por forma (algumas precisam de folga p/ a forma caber
// em volta do texto — losango, cilindro, documento, paralelogramo).
const SHAPE_PADDING: Record<FlowShapeKind, string> = {
  round: "px-5 py-1.5",
  rect: "px-3.5 py-2",
  diamond: "px-9 py-5",
  parallelogram: "px-6 py-2",
  doubleBar: "px-5 py-2",
  cylinder: "px-4 pb-2 pt-3.5",
  document: "px-3.5 pb-4 pt-2",
  circle: "h-11 w-11 items-center justify-center p-0 text-center",
};

// Desenha a forma num viewBox 0..100 x 0..60, esticada ao box do nó
// (preserveAspectRatio="none") com stroke não-escalável (~2px sempre).
export function FlowShapeSvg({
  shapeKind,
  stroke,
  fill,
  className,
}: {
  shapeKind: FlowShapeKind;
  stroke: string;
  fill: string;
  className?: string;
}) {
  const p = { fill, stroke, strokeWidth: 2, vectorEffect: "non-scaling-stroke" as const };
  let body: ReactNode;
  switch (shapeKind) {
    case "round": // estádio (início/fim)
      body = <rect x={1} y={1} width={98} height={58} rx={29} ry={29} {...p} />;
      break;
    case "rect":
      body = <rect x={1} y={1} width={98} height={58} rx={7} {...p} />;
      break;
    case "diamond":
      body = <polygon points="50,1 99,30 50,59 1,30" {...p} />;
      break;
    case "parallelogram":
      body = <polygon points="20,1 99,1 80,59 1,59" {...p} />;
      break;
    case "doubleBar": // subprocesso: retângulo com barras laterais
      body = (
        <>
          <rect x={1} y={1} width={98} height={58} rx={4} {...p} />
          <line x1={9} y1={1} x2={9} y2={59} {...p} />
          <line x1={91} y1={1} x2={91} y2={59} {...p} />
        </>
      );
      break;
    case "cylinder": // banco de dados
      body = (
        <>
          <path d="M2 10 v40 a48 8 0 0 0 96 0 v-40" {...p} />
          <ellipse cx={50} cy={10} rx={48} ry={8} {...p} />
        </>
      );
      break;
    case "document": // base ondulada
      body = <path d="M1 1 h98 v46 q-24.5 12 -49 0 t-49 0 V1 Z" {...p} />;
      break;
    case "circle": // conector
      body = <circle cx={50} cy={30} r={28} {...p} />;
      break;
  }
  return (
    <svg
      viewBox="0 0 100 60"
      preserveAspectRatio="none"
      className={className}
      aria-hidden
      xmlns="http://www.w3.org/2000/svg"
    >
      {body}
    </svg>
  );
}

// Frame de um nó de fluxograma: SVG de fundo com a forma + conteúdo por cima.
export function FlowShapeFrame({
  flowType,
  selected,
  isDropTarget,
  effColor,
  onDoubleClick,
  children,
}: {
  flowType: FlowType;
  selected: boolean;
  isDropTarget: boolean;
  effColor?: string;
  onDoubleClick?: (e: React.MouseEvent) => void;
  children: ReactNode;
}) {
  const meta = FLOW_TYPES[flowType];
  const active = isDropTarget || selected;
  // Prioridade da cor: seleção/drop → primária; cor própria do nó → ela; senão
  // a cor do tipo.
  const stroke = active ? PRIMARY : effColor || colorHex(meta.color);
  const fill = active ? "rgb(var(--color-primary) / 0.10)" : `${colorHex(meta.color)}1f`; // 1f ≈ 12%
  const pad = SHAPE_PADDING[meta.shapeKind];
  // `isolate` cria contexto de empilhamento; o SVG vai p/ `-z-10` (atrás do
  // conteúdo). Os handles ficam como FILHOS DIRETOS do box (não num wrapper) —
  // o React Flow precisa disso p/ medir os handleBounds e rotear as arestas.
  return (
    <div
      onDoubleClick={onDoubleClick}
      className={["relative isolate flex items-center justify-center text-center", pad].join(" ")}
    >
      <FlowShapeSvg
        shapeKind={meta.shapeKind}
        stroke={stroke}
        fill={fill}
        className="pointer-events-none absolute inset-0 -z-10 h-full w-full"
      />
      {children}
    </div>
  );
}

// Handles: todo tipo mantém os 6 ids (tl/tt/tr, sr/sb/sl) — NUNCA renomear,
// senão as arestas antigas ficam órfãs. start esconde os targets; end esconde os
// sources. Retorna se um lado deve ficar oculto/desconectável.
export function flowHandleHidden(flowType: FlowType, handleType: "source" | "target"): boolean {
  const meta = FLOW_TYPES[flowType];
  if (handleType === "target") return meta.noIncoming === true; // start
  return meta.noOutgoing === true; // end
}

// Rótulo do ramo (Sim/Não) de um handle source de um decision. undefined se não
// for decision ou o handle não for um ramo.
export function branchLabel(flowType: FlowType, handleId: string): string | undefined {
  return FLOW_TYPES[flowType].branches?.find((b) => b.handleId === handleId)?.label;
}

// Validação de conexão no flow: start não recebe entrada, end não emite saída.
export function makeIsValidConnection(getNode: (id: string) => MindNode | undefined): IsValidConnection {
  return (c) => {
    if (!c.source || !c.target || c.source === c.target) return false;
    const s = getNode(c.source);
    const t = getNode(c.target);
    if (s && FLOW_TYPES[resolveFlowType(s)].noOutgoing) return false; // end como origem
    if (t && FLOW_TYPES[resolveFlowType(t)].noIncoming) return false; // start como alvo
    return true;
  };
}

// Preview pequeno da forma (paleta / badge). Usa a mesma FlowShapeSvg.
export function FlowTypePreview({ flowType, className }: { flowType: FlowType; className?: string }) {
  const meta = FLOW_TYPES[flowType];
  const hex = colorHex(meta.color);
  return (
    <FlowShapeSvg
      shapeKind={meta.shapeKind}
      stroke={hex}
      fill={`${hex}22`}
      className={className ?? "h-5 w-8 shrink-0"}
    />
  );
}
